import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useRolodexEntries } from 'features/rolodex'
import { useMemo } from 'react'
import useSWR from 'swr'
import {
  BulkDamageAreas,
  BulkDamageAreasWithHealthConditions,
  CentralizedHealthScore,
  DamageInfo,
  DamageInfoWithHealthCondition,
  ErrorInfo,
  GeckoRiskAssessment,
  GeckoUnit,
  PlanMinRlsInfo,
  RemainingLifeInfo,
  RolodexEntryType,
} from 'types'

import { calculateHealthCondition } from './centralizedHealthCondition'

export function GetComponentPercentBelowTmin(componentId: string | undefined) {
  const { authorizedAxios: axios } = useGeckoAuth()
  const { RISK_CALCULATOR_URL } = useRuntimeVariables()
  const endpoint = `${RISK_CALCULATOR_URL}/api/belowTmin?component=${componentId}`

  return useSWR<{
    totalCount: number
    belowTminCount: number
    percentBelowTmin: number
  }>(componentId ? [componentId] : null, () =>
    axios
      .get(endpoint)
      .then((res) => (typeof res.data === 'string' ? {} : res.data))
  )
}

export function useGetBulkComponentMinRls(componentIds: string[]) {
  const { authorizedAxios: axios } = useGeckoAuth()
  const { RISK_CALCULATOR_URL } = useRuntimeVariables()
  const endpoint = `${RISK_CALCULATOR_URL}/api/bulkMinRemainingLife`
  const postBody = {
    components: componentIds,
  }

  return useSWR<{
    [componentId: string]: RemainingLifeInfo | ErrorInfo
  }>(componentIds.length ? ['minRL', componentIds] : null, () =>
    axios
      .post(endpoint, postBody)
      .then((res) => (typeof res.data === 'string' ? {} : res.data))
  )
}

export function useGetBulkPlanMinRls(planIds: string[]) {
  const { authorizedAxios: axios } = useGeckoAuth()
  const { RISK_CALCULATOR_URL } = useRuntimeVariables()
  const endpoint = `${RISK_CALCULATOR_URL}/api/bulkCalculateScenarioCascade`
  const postBody = {
    plans: planIds,
  }

  return useSWR<{
    [planId: string]: {
      [componentId: string]: PlanMinRlsInfo | ErrorInfo
    } | null
  }>(planIds.length ? [planIds] : null, () =>
    axios
      .post(endpoint, postBody)
      .then((res) => (typeof res.data === 'string' ? {} : res.data))
  )
}

export const DEFAULT_PLAN_KEY = 'baseData'

/**
 * This function returns the damage areas and RL stats for a list of components and plans.
 *
 * The return signature is a map of componentIds to a map of planKeys which contains the damage areas and RL stats.
 *
 * If no planId is supplied, the planKey in the component object is the constant DEFAULT_DAMAGE_AREAS_PLAN_KEY
 *
 */
export function useGetBulkDamageAreas(
  componentAndPlanList: {
    componentId: string
    planId?: string
  }[],
  timeHorizon?: number,
  riskScoreMethod?: string,
  useRiskAssessment?: boolean
): ReturnType<typeof useSWR<BulkDamageAreas>> {
  const { authorizedAxios: axios } = useGeckoAuth()
  const { RISK_CALCULATOR_URL } = useRuntimeVariables()
  const endpoint = `${RISK_CALCULATOR_URL}/api/bulkCalculateDamageAreas`
  const postBody = {
    componentAndPlanList: componentAndPlanList,
    timeHorizon: timeHorizon,
    riskScoreMethod: riskScoreMethod,
    useRiskAssessment: useRiskAssessment,
  }

  return useSWR<{
    [componentId: string]: {
      [planKey: string | typeof DEFAULT_PLAN_KEY]: DamageInfo | ErrorInfo
    }
  }>(
    componentAndPlanList.length
      ? [componentAndPlanList, riskScoreMethod, timeHorizon]
      : null,
    () =>
      axios
        .post(endpoint, postBody)
        .then((res) => (typeof res.data === 'string' ? {} : res.data))
  )
}

export function isErrorInfo(
  value: DamageInfo | RemainingLifeInfo | PlanMinRlsInfo | ErrorInfo
): value is ErrorInfo {
  return 'error' in value
}

/**
 * Enhanced version of useGetBulkDamageAreas that calculates health conditions for each component/plan/year.
 *
 * This wrapper function:
 * 1. Calls useGetBulkDamageAreas to get damage data
 * 2. For each component/plan/year combination, calculates health condition using calculateHealthCondition
 * 3. Populates the healthCondition field in the damage data
 *
 * @param componentAndPlanList - List of component/plan combinations
 * @param timeHorizon - Time horizon for calculations
 * @param riskScoreMethod - Risk score calculation method
 * @param useRiskAssessment - Whether to use risk assessment
 * @param hasInspectionMap - Map of componentId to inspection status
 * @returns Enhanced damage data with health conditions populated
 */
export function useGetBulkDamageAreasWithHealthConditions(
  componentAndPlanList: {
    componentId: string
    planId?: string
  }[],
  timeHorizon?: number,
  riskScoreMethod?: string,
  useRiskAssessment?: boolean,
  hasInspectionMap?: Map<string, boolean>
): {
  data: BulkDamageAreasWithHealthConditions | undefined
  error: Error | undefined
  isLoading: boolean
  isValidating: boolean
  mutate: (
    data?:
      | BulkDamageAreasWithHealthConditions
      | Promise<BulkDamageAreasWithHealthConditions>,
    shouldRevalidate?: boolean
  ) => Promise<BulkDamageAreasWithHealthConditions | undefined>
} {
  const damageAreasResult = useGetBulkDamageAreas(
    componentAndPlanList,
    timeHorizon,
    riskScoreMethod,
    useRiskAssessment
  )

  // Transform the data to include health conditions
  const enhancedData = useMemo(():
    | BulkDamageAreasWithHealthConditions
    | undefined => {
    if (!damageAreasResult.data) {
      return undefined
    }

    const enhanced: BulkDamageAreasWithHealthConditions = Object.fromEntries(
      Object.entries(damageAreasResult.data).map(
        ([componentId, componentData]) => {
          if (!componentData) return [componentId, componentData]

          const enhancedComponentData = Object.fromEntries(
            Object.entries(componentData).map(([planKey, planData]) => {
              if (!planData || isErrorInfo(planData)) {
                return [planKey, planData]
              }

              const damageInfo = planData as DamageInfo
              const enhancedDamageInfo: DamageInfoWithHealthCondition =
                Object.fromEntries(
                  Object.entries(damageInfo).map(([year, yearData]) => {
                    if (!yearData) return [year, yearData]

                    // Calculate health condition using the centralized function
                    const hasInspection =
                      hasInspectionMap?.get(componentId) ?? false

                    const componentHealthCondition: CentralizedHealthScore =
                      calculateHealthCondition({
                        minRL: yearData.minRl,
                        riskScore: yearData.riskScore,
                        hasInspection,
                      })

                    // Add health condition to the year data
                    return [
                      year,
                      {
                        ...yearData,
                        componentHealthCondition,
                      },
                    ]
                  })
                )

              return [planKey, enhancedDamageInfo]
            })
          )

          return [componentId, enhancedComponentData]
        }
      )
    )

    return enhanced
  }, [damageAreasResult.data, hasInspectionMap])

  return {
    data: enhancedData,
    error: damageAreasResult.error,
    isLoading: damageAreasResult.isLoading,
    isValidating: damageAreasResult.isValidating,
    mutate: async (
      data?:
        | BulkDamageAreasWithHealthConditions
        | Promise<BulkDamageAreasWithHealthConditions>,
      shouldRevalidate?: boolean
    ) => {
      // Since we can't directly mutate the enhanced data, we trigger a revalidation
      // of the underlying SWR hook which will cause our useMemo to recalculate
      await damageAreasResult.mutate(undefined, shouldRevalidate)
      return enhancedData
    },
  }
}

export function useGetBulkDamageAreasWithHealthConditionsForSite(
  site: string,
  timeHorizon?: number,
  riskScoreMethod?: string,
  useRiskAssessment?: boolean
): {
  data: BulkDamageAreasWithHealthConditions | undefined
  error: Error | undefined
  isLoading: boolean
  isValidating: boolean
  mutate: (
    data?:
      | BulkDamageAreasWithHealthConditions
      | Promise<BulkDamageAreasWithHealthConditions>,
    shouldRevalidate?: boolean
  ) => Promise<BulkDamageAreasWithHealthConditions | undefined>
} {
  const {
    units,
    components,
    inspections,
    entries,
    isLoading: isRolodexLoading,
  } = useRolodexEntries({
    type: [
      RolodexEntryType.UNIT,
      RolodexEntryType.COMPONENT,
      RolodexEntryType.INSPECTION,
      RolodexEntryType.RISK_ASSESSMENT,
    ],
    tags: { site: [site] },
  })

  const riskAssessments = useMemo(() => {
    return entries
      .filter((entry) => entry.type === RolodexEntryType.RISK_ASSESSMENT)
      .map((entry) => entry as GeckoRiskAssessment)
  }, [entries])

  const hasInspectionMap = useMemo(() => {
    const map = new Map<string, boolean>()

    const componentToUnit = new Map<string, string>()
    components.forEach((component) => {
      const unitId = component.tags[RolodexEntryType.UNIT]
      if (unitId) {
        componentToUnit.set(component.id, unitId)
      }
    })

    const unitsWithInspections = new Set<string>()
    inspections.forEach((inspection) => {
      const componentId = inspection.tags[RolodexEntryType.COMPONENT]
      if (componentId) {
        const unitId = componentToUnit.get(componentId)
        if (unitId) {
          unitsWithInspections.add(unitId)
        }
      }
    })

    units.forEach((unit) => {
      map.set(unit.id, unitsWithInspections.has(unit.id))
    })

    return map
  }, [units, components, inspections])

  const componentAndPlanList = useMemo(() => {
    return components.map((component) => ({
      componentId: component.id,
      planId: undefined,
    }))
  }, [components])

  const componentResult = useGetBulkDamageAreasWithHealthConditions(
    componentAndPlanList,
    timeHorizon,
    riskScoreMethod,
    useRiskAssessment,
    hasInspectionMap
  )

  const unitToRiskAssessment = useMemo(() => {
    const map = new Map<string, GeckoRiskAssessment>()
    riskAssessments.forEach((ra: GeckoRiskAssessment) => {
      const unitId = ra.tags[RolodexEntryType.UNIT]
      if (unitId) {
        map.set(unitId, ra)
      }
    })
    return map
  }, [riskAssessments])

  const unitsWithComponents = useMemo(() => {
    const set = new Set<string>()
    components.forEach((component) => {
      const unitId = component.tags[RolodexEntryType.UNIT]
      if (unitId) {
        set.add(unitId)
      }
    })
    return set
  }, [components])

  const enhancedData = useMemo(():
    | BulkDamageAreasWithHealthConditions
    | undefined => {
    if (isRolodexLoading || !componentResult.data) {
      return undefined
    }

    const result: BulkDamageAreasWithHealthConditions = {
      ...componentResult.data,
    }

    units.forEach((unit: GeckoUnit) => {
      if (!unitsWithComponents.has(unit.id)) {
        const riskAssessment = unitToRiskAssessment.get(unit.id)

        if (riskAssessment?.data) {
          const riskScore =
            riskAssessment.data.current_risk ||
            riskAssessment.data.predicted_risk

          if (riskScore) {
            const currentYear = new Date().getFullYear()
            const hasInspection = hasInspectionMap.get(unit.id) ?? false

            const unitHealthScoreForYear: CentralizedHealthScore =
              calculateHealthCondition({
                minRL: undefined,
                riskScore: riskScore,
                hasInspection,
              })

            const damageInfo: DamageInfoWithHealthCondition = {}

            const yearsToCreate = timeHorizon ? timeHorizon + 1 : 1
            for (let i = 0; i < yearsToCreate; i++) {
              const year = currentYear + i
              damageInfo[year.toString()] = {
                riskScore: riskScore,
                minRl: undefined,
                percentBelowTmin: undefined,
                damageAreas: undefined,
                healthScore: undefined,
                componentHealthCondition: unitHealthScoreForYear,
              }
            }

            result[unit.id] = {
              [DEFAULT_PLAN_KEY]: damageInfo,
            }
          }
        }
      }
    })

    return result
  }, [
    isRolodexLoading,
    componentResult.data,
    units,
    unitsWithComponents,
    unitToRiskAssessment,
    hasInspectionMap,
    timeHorizon,
  ])

  return {
    data: enhancedData,
    error: componentResult.error,
    isLoading: isRolodexLoading || componentResult.isLoading,
    isValidating: componentResult.isValidating,
    mutate: async (
      data?:
        | BulkDamageAreasWithHealthConditions
        | Promise<BulkDamageAreasWithHealthConditions>,
      shouldRevalidate?: boolean
    ) => {
      await componentResult.mutate(data, shouldRevalidate)
      return enhancedData
    },
  }
}
